import { useUser } from "@playlive/react-query";
import { CheckCircle } from "flowbite-react-icons/solid";

import useTiltifyConfig from "@/hooks/useTiltifyConfig";
import { useTiltifyLifetimeRaised } from "@/hooks/useTiltifyLifetimeRaised";
import { formatCurrencyNumber } from "@/util/common";
import stJudeLogo from "../../../assets/logos/sj_logo.svg";
import { TiltifyLogo } from "../svg/tiltify-logo";
import { LoadingSpinner } from "../v2/loading-spinner";
import { Hover3dCard } from "./hover3d.card";

export function TiltifyProfileCard({
  onCardFlip,
  flipped = false,
  overrideUserSlug,
}: {
  onCardFlip?: (flipped: boolean) => void;
  flipped?: boolean;
  overrideUserSlug?: string | null;
}) {
  const { config: tiltifyConfig } = useTiltifyConfig();

  const { data: currentUser } = useUser(
    {
      userSlug: overrideUserSlug ?? tiltifyConfig.tiltifyUserSlug,
      charityType: "tiltify",
    },
    {
      refetchInterval: false,
    },
  );

  const currentUserAvatar = currentUser?.avatar;

  const { amountRaised, isError } = useTiltifyLifetimeRaised({
    tiltifySlug: overrideUserSlug ?? currentUser?.username ?? "n/a",
    isTeam: false,
  });

  const isAuthenticated = Boolean(
    tiltifyConfig.tiltifyUsername && tiltifyConfig.tiltifyAccessToken,
  );

  // Show the spinner until the first successful fetch settles (amountRaised >= 0).
  // Using amountRaised < 0 && !isError (rather than isFetching && amountRaised < 0)
  // ensures the spinner is shown on the very first render, before React Query has had
  // a chance to kick off the background fetch (isFetching is false on first render
  // when initialData is used, since the fetch is scheduled asynchronously).
  const showLoadingAmountRaised = (amountRaised ?? -1) < 0 && !isError;
  const displayAmount = (amountRaised ?? -1) >= 0 ? `$${formatCurrencyNumber(amountRaised)}` : "—";

  const defaultCard = (
    <Hover3dCard>
      {currentUser && (
        <div className="relative h-full w-full tiltify-gradient text-neutral-content">
          {currentUserAvatar && (
            <img
              className="z-20 w-16.25 opacity-80 h-16.25 rounded-xl absolute top-5 right-5 border-4 border-base-100 shadow-lg drag-none"
              src={currentUserAvatar.src}
              alt={currentUserAvatar.alt}
            />
          )}
          <div className="absolute top-5 left-5 flex flex-col z-20 ">
            <TiltifyLogo className="w-42.5 h-10.5 text-neutral-content opacity-75" />
            <div className="text-lg opacity-40 text-neutral-content">
              {currentUser.url.slice(1)}
            </div>
          </div>
          <img
            src={stJudeLogo}
            alt="St. Jude Logo"
            className="absolute bottom-2 right-2 w-22 h-auto opacity-20"
          />
          <div className="mb-4 absolute bottom-0 left-4 flex items-center">
            <div
              className={`flex flex-col transition-opacity ${showLoadingAmountRaised ? "opacity-0" : "opacity-100"}`}
            >
              <span className="text-2xl font-bold text-success">{displayAmount}</span>
              <span className="text-xs opacity-50">Total Amount Raised</span>
            </div>
          </div>
          {showLoadingAmountRaised && (
            <div className="mb-4 absolute bottom-0 left-4 flex items-center">
              <LoadingSpinner />
            </div>
          )}
        </div>
      )}
    </Hover3dCard>
  );

  const otherCard = (
    <Hover3dCard>
      {currentUser && (
        <div className="relative h-full w-full">
          <div className="absolute top-3 right-3 flex flex-col">
            <TiltifyLogo className="w-35 h-8 opacity-75" />
          </div>
          <div className="absolute top-3 left-3 flex flex-col">
            {isAuthenticated ? (
              <div className="text-success">
                <CheckCircle />
              </div>
            ) : (
              <LoadingSpinner size="sm" />
            )}
          </div>
          <div className="flex flex-col absolute bottom-4 left-4">
            <div className="flex flex-col">
              <span className="opacity-30 text-xs">Slug</span>
              <span className="opacity-40 text-sm">{currentUser.url.slice(1)}</span>
            </div>
            <div className="flex flex-col mt-3">
              <span className="opacity-30 text-xs">Username</span>
              <span className="opacity-40 text-sm">{currentUser.username}</span>
            </div>
            <div className="flex flex-col mt-3">
              <span className="opacity-30 text-xs">ID</span>
              <span className="opacity-40 text-sm">{currentUser.id}</span>
            </div>
          </div>
        </div>
      )}
    </Hover3dCard>
  );

  if (onCardFlip) {
    return (
      <div className="flex flex-col">
        <div className="mt-10 mb-0">
          <label className="swap swap-flip">
            <input
              checked={flipped}
              onChange={(event) => {
                onCardFlip(event.target.checked);
              }}
              type="checkbox"
            />
            <div className="swap-on">{otherCard}</div>
            <div className="swap-off">{defaultCard}</div>
          </label>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="mt-10 mb-0">
        <label className="swap swap-flip">
          <input type="checkbox" />
          <div className="swap-on">{otherCard}</div>
          <div className="swap-off">{defaultCard}</div>
        </label>
      </div>
    </div>
  );
}
